import Vue from 'vue'
import axios from 'axios'
import settings from './settings.js'
import { message } from '@/util/message'
import qs from 'qs'

axios.defaults.timeout = 20000
axios.defaults.baseURL = settings.gbs.host
Vue.prototype.$axios = axios

const JSON_CONTENT_TYPE = 'application/json; charset=UTF-8'
const FORM_CONTENT_TYPE = 'application/x-www-form-urlencoded; charset=UTF-8'
const LOGIN_WHITE_LIST = [
  'app/rest/school/green/security/loginCourseNew',
  'app/rest/school/green/security/loginCourse2',
  'app/rest/school/green/security/loginWithCode',
  'app/rest/school/green/security/validCodeKey',
  'app/rest/school/green/security/loginValidateCodeNew',
  'app/rest/school/green/security/loginValidateCodeByManager',
  'app/rest/school/green/security/resetPassword'
]

let currentAjaxVm = null
let hasBoundInterceptors = false

function isObjectData (data) {
  return data && typeof data === 'object' && !Array.isArray(data)
}

function setAjaxLoading (vm, loading) {
  if (vm && vm.$store && vm.$store.dispatch) {
    vm.$store.dispatch('setAjaxLoading', loading)
  }
}

function runCallback (vm, fn, payload) {
  if (typeof fn !== 'function') return
  if (vm && vm.$nextTick) {
    vm.$nextTick(() => {
      fn(payload)
    })
    return
  }
  fn(payload)
}

function runErrorCallback (vm, errFn, response) {
  if (errFn && typeof errFn === 'function') {
    errFn.call(vm, response)
  }
}

function hasToken (vm) {
  return !!(
    vm &&
    vm.$store &&
    vm.$store.state &&
    vm.$store.state.login &&
    vm.$store.state.login.token
  )
}

function getToken (vm) {
  return hasToken(vm) ? vm.$store.state.login.token : ''
}

function isPublicUrl (url) {
  const requestUrl = String(url || '')
  if (requestUrl.indexOf('app/rest/open/') > -1) return true
  return LOGIN_WHITE_LIST.indexOf(requestUrl) > -1
}

function resetLoginStatus (vm) {
  if (!vm || !vm.$store || !vm.$store.dispatch) return
  vm.$store.dispatch('resetToken').then(() => {
    if (window.location.pathname.indexOf('login') > -1) return
    setTimeout(() => {
      location.reload()
    }, 100)
  })
}

function showRequestError (error) {
  const response = error && error.response ? error.response : null
  const res = response && response.data ? response.data : null
  if (res) {
    message({
      message: res.message || res,
      type: 'error',
      duration: 1500
    })
    return
  }
  message({
    message: '请求出错，请重试',
    type: 'error',
    duration: 1500
  })
}

function ensureInterceptors () {
  if (hasBoundInterceptors) return

  axios.interceptors.request.use(
    config => {
      const vm = currentAjaxVm
      if (!vm) return config

      if (!hasToken(vm) && !isPublicUrl(config.url)) {
        setAjaxLoading(vm, false)
        if (vm.$router) vm.$router.push('/login')
        return Promise.reject(new Error('NO_TOKEN'))
      }

      const token = getToken(vm)
      if (token) {
        if (!config.headers) config.headers = {}
        if (config.headers.common) {
          config.headers.common.Authorization = token
        } else {
          config.headers.Authorization = token
        }
      }

      return config
    },
    err => {
      setAjaxLoading(currentAjaxVm, false)
      return Promise.reject(err)
    }
  )

  axios.interceptors.response.use(
    response => response,
    error => {
      const vm = currentAjaxVm
      const response = error && error.response ? error.response : null
      const res = response && response.data ? response.data : null

      if (error && error.message !== 'NO_TOKEN') {
        showRequestError(error)
      }

      if (res && res.status === 401) {
        resetLoginStatus(vm)
      }

      setAjaxLoading(vm, false)
      return Promise.reject(error)
    }
  )

  hasBoundInterceptors = true
}

function resolveBaseURL (data, reqType) {
  if (reqType === 'double-teacher') return settings.gbs.doubleTeacher
  if (isObjectData(data) && data.ajaxType === 'idCard') {
    return settings.gbs.idCard
  }
  return settings.gbs.host
}

function shouldUseGetBody (type, data, reqType) {
  return type === 'get' && isObjectData(data) &&
    (reqType === 'body' || data.useBody === true)
}

function buildPayload (type, data, useGetBody) {
  if (type === 'get' || type === 'delete') {
    return useGetBody ? data : { params: data }
  }
  return data
}

function normalizePayload (payload, contentType) {
  let nextPayload = payload
  let nextContentType = contentType

  if (isObjectData(nextPayload) && nextPayload.ajaxType === 'form') {
    nextContentType = FORM_CONTENT_TYPE
    nextPayload = qs.stringify(nextPayload)
  }

  return {
    payload: nextPayload,
    contentType: nextContentType
  }
}

function appendSequenceQuery (url, payload) {
  const query = qs.stringify(payload)
  if (!query) {
    return {
      url: url,
      payload: payload
    }
  }

  const prefix = url.indexOf('?') > -1 ? '&' : '?'
  return {
    url: url + prefix + query,
    payload: prefix + query
  }
}

function isDownloadRequest (payload) {
  return isObjectData(payload) && payload.down
}

function finishSuccess (vm, fn, payload) {
  setAjaxLoading(vm, false)
  runCallback(vm, fn, payload)
}

function finishError (vm, errFn, error) {
  const response = error && error.response ? error.response : null
  runErrorCallback(vm, errFn, response)
}

function requestWithGetBody (vm, url, payload, contentType, fn, errFn) {
  return axios({
    method: 'get',
    url: url,
    data: payload,
    headers: {
      'Content-Type': contentType
    },
    transformRequest: [(data, headers) => {
      if (headers && headers.common) {
        delete headers.common['Content-Type']
      }
      if (headers && headers['Content-Type'] === 'application/json') {
        return JSON.stringify(data)
      }
      return data
    }],
    paramsSerializer: null
  }).then((res) => {
    finishSuccess(vm, fn, res.data)
  }).catch((error) => {
    finishError(vm, errFn, error)
  })
}

function requestDownload (vm, url, payload, fn) {
  return axios({
    method: 'post',
    url: url,
    params: payload,
    responseType: 'blob',
    timeout: 50000
  }).then((res) => {
    finishSuccess(vm, fn, res.data)
  }).catch(() => {
    setAjaxLoading(vm, false)
  })
}

function buildDoubleTeacherUrl (url, type, data) {
  let nextUrl = url
  if (typeof data === 'string') {
    nextUrl += '/' + data
    return nextUrl
  }

  if (type === 'get' && data) {
    let query = new URLSearchParams(data).toString()
    if (query) {
      query = nextUrl.indexOf('?') > -1 ? '&' + query : '?' + query
      nextUrl += query
    }
  }

  return nextUrl
}

function requestDoubleTeacher (vm, type, url, data, payload, contentType, fn, errFn) {
  const requestUrl = buildDoubleTeacherUrl(url, type, data)
  return axios({
    method: type,
    url: requestUrl,
    data: type === 'get' ? null : payload,
    withCredentials: true,
    headers: {
      'Content-Type': contentType
    }
  }).then((res) => {
    setAjaxLoading(vm, false)
    runCallback(vm, fn, res.data)
    if (res.data && !res.data.success) {
      if (
        res.data.code === 10010 ||
        res.data.code === 10011 ||
        res.data.code === 10012
      ) {
        vm.$message({
          showClose: true,
          message: '双师' + res.data.message + '，请重新登录',
          type: 'error'
        })
      }
    }
  }).catch((error) => {
    finishError(vm, errFn, error)
  })
}

function requestDefault (vm, type, url, payload, contentType, fn, errFn) {
  const config = {
    method: type,
    url: url,
    headers: {
      'Content-Type': contentType
    }
  }
  if (type === 'get' || type === 'delete') {
    if (payload && payload.params) {
      config.params = payload.params
    }
  } else {
    config.data = payload
  }

  return axios(config).then((res) => {
    finishSuccess(vm, fn, res.data)
  }).catch((error) => {
    finishError(vm, errFn, error)
  })
}

/**
 * 封装axios的通用请求
 * @param  {string}   type      get或post
 * @param  {string}   url       请求的接口URL
 * @param  {object}   data      传的参数，没有则传空对象
 * @param  {Function} fn        回调函数
 * @param  {boolean}  tokenFlag 是否需要携带token参数，为true，不需要；false，需要。一般除了登录，都需要
 */
export default function (type, url, data, fn, tokenFlag, errFn, reqType, ck) {
  console.log(type, url, data, fn, tokenFlag, errFn, reqType, ck, '=====')
  const vm = this
  const requestData = data || {}
  const useGetBody = shouldUseGetBody(type, requestData, reqType)

  void tokenFlag
  void ck

  currentAjaxVm = vm
  ensureInterceptors()

  setAjaxLoading(vm, true)
  axios.defaults.baseURL = resolveBaseURL(requestData, reqType)

  let contentType = JSON_CONTENT_TYPE
  if (reqType === 'double-teacher' && url === 'login') {
    contentType = FORM_CONTENT_TYPE
  }

  let payload = buildPayload(type, requestData, useGetBody)
  const normalized = normalizePayload(payload, contentType)
  payload = normalized.payload
  contentType = normalized.contentType

  if (reqType === 'sequence') {
    const sequenceResult = appendSequenceQuery(url, payload)
    url = sequenceResult.url
    payload = sequenceResult.payload
  }

  if (isDownloadRequest(payload)) {
    return requestDownload(vm, url, payload, fn)
  }

  if (reqType === 'double-teacher') {
    return requestDoubleTeacher(vm, type, url, requestData, payload, contentType, fn, errFn)
  }

  if (useGetBody) {
    return requestWithGetBody(vm, url, payload, contentType, fn, errFn)
  }

  return requestDefault(vm, type, url, payload, contentType, fn, errFn)
}

export function getSSE (url, params, handlers, reqType) {
  const that = this
  const base = reqType && reqType === 'body'
    ? settings.gbs.doubleTeacher
    : settings.gbs.host
  const isAbs = /^https?:\/\//.test(url)
  let full = isAbs
    ? url
    : base.replace(/\/+$/, '') + '/' + String(url || '').replace(/^\/+/, '')
  const qsStr = new URLSearchParams(params || {}).toString()
  if (qsStr) full += (full.indexOf('?') >= 0 ? '&' : '?') + qsStr
  const token = that && that.$store && that.$store.state && that.$store.state.login
    ? that.$store.state.login.token
    : ''
  if (token && full.indexOf('Authorization=') < 0 && full.indexOf('token=') < 0) {
    full += (full.indexOf('?') >= 0 ? '&' : '?') + 'Authorization=' + encodeURIComponent(token)
  }
  const es = new EventSource(full, { withCredentials: true })
  if (handlers && handlers.onOpen) es.onopen = handlers.onOpen
  if (handlers && handlers.onMessage) es.onmessage = handlers.onMessage
  if (handlers && handlers.onError) es.onerror = handlers.onError
  return es
}
export function getSSEStream (url, params, handlers) {
  const that = this
  const base = settings.gbs.host
  const isAbs = /^https?:\/\//.test(url)
  let full = isAbs
    ? url
    : base.replace(/\/+$/, '') + '/' + String(url || '').replace(/^\/+/, '')
  const p = params || {}
  const keys = Object.keys(p)
  const parts = []
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]
    const v = p[k]
    if (v == null) continue
    if (Array.isArray(v)) {
      const prim = v.every(x => x == null || typeof x !== 'object')
      if (prim) {
        v.forEach(x => {
          parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(x)))
        })
      } else {
        parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(JSON.stringify(v)))
      }
    } else if (typeof v === 'object') {
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(JSON.stringify(v)))
    } else {
      parts.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(v)))
    }
  }
  const qsStr = parts.join('&')
  if (qsStr) full += (full.indexOf('?') >= 0 ? '&' : '?') + qsStr
  const token = that && that.$store && that.$store.state && that.$store.state.login
    ? that.$store.state.login.token
    : ''
  const ctrl = new AbortController()
  const headers = { 'Content-Type': 'text/event-stream' }
  if (token) headers.Authorization = String(token)

  const dec = new TextDecoder()
  fetch(full, {
    method: 'GET',
    headers,
    credentials: 'include',
    signal: ctrl.signal
  }).then(async (resp) => {
    if (handlers && handlers.onOpen) handlers.onOpen()
    const rd = resp && resp.body ? resp.body.getReader() : null
    if (!rd) throw new Error('SSE no body')
    let buf = ''
    while (true) {
      const { value, done } = await rd.read()
      if (done) break
      buf += dec.decode(value)
      let idx = buf.indexOf('\n\n')
      while (idx >= 0) {
        const chunk = buf.slice(0, idx)
        buf = buf.slice(idx + 2)
        const lines = chunk.split('\n')
        const arr = []
        for (let k = 0; k < lines.length; k++) {
          const line = lines[k]
          if (line.indexOf('data:') === 0) arr.push(line.slice(5).trim())
        }
        const data = arr.join('\n')
        if (handlers && handlers.onMessage) handlers.onMessage({ data })
        idx = buf.indexOf('\n\n')
      }
    }
  }).catch((err) => {
    if (handlers && handlers.onError) handlers.onError(err)
  })
  return { close: () => ctrl.abort() }
}
